/**
 * Single source of truth for currency handling in the admin dashboard.
 *
 * Amounts are stored in BGN (base currency). EUR is derived for display.
 * To drop BGN platform-wide once the eurozone transition is complete, remove
 * "BGN" from DISPLAY_CURRENCIES — that is the single lever.
 */

/** Currency that monetary amounts are stored in (DB source of truth). */
export const BASE_CURRENCY = "BGN";

/**
 * Ordered list of currencies to display. The first entry is primary.
 * EUR-first for the eurozone transition. Remove "BGN" to show EUR only.
 */
export const DISPLAY_CURRENCIES = ["EUR", "BGN"];

/** Official fixed eurozone conversion rate: 1 EUR = 1.95583 BGN. */
export const BGN_PER_EUR = 1.95583;

/** Currencies selectable in admin financial settings. */
export const SUPPORTED_CURRENCIES = ["EUR", "BGN", "USD"];

const RATES_TO_BGN = {
  BGN: 1,
  EUR: BGN_PER_EUR,
  USD: 1,
};

const round2 = (n) => Number(Number(n || 0).toFixed(2));

/** Convert an amount between two currencies via the fixed BGN bridge. */
export const convert = (amount, from, to) => {
  if (from === to) return round2(amount);
  const inBgn = amount * RATES_TO_BGN[from];
  return round2(inBgn / RATES_TO_BGN[to]);
};

export const toEUR = (bgn) => convert(bgn, "BGN", "EUR");
export const toBGN = (eur) => convert(eur, "EUR", "BGN");

const FORMATTERS = {
  EUR: (value) => `${value} €`,
  BGN: (value, locale) => (locale === "en" ? `BGN ${value}` : `${value} лв.`),
  USD: (value) => `$${value}`,
};

/**
 * Format a stored amount across all DISPLAY_CURRENCIES, in order.
 *
 * @example formatMoney(38.27) // "19.57 € / 38.27 лв."
 */
export const formatMoney = (amount, options = {}) => {
  const { base = BASE_CURRENCY, locale = "en", separator = " / " } = options;
  return DISPLAY_CURRENCIES.map((code) =>
    FORMATTERS[code](convert(amount, base, code).toFixed(2), locale),
  ).join(separator);
};
